import type { TFunction } from 'i18next';
import type {
  SmartDimension,
  SmartFilterCode,
  SmartMetricOption,
  SmartReportArea,
  SmartRow,
} from '@optiflow/types';
import { failureReasonLabel } from '../components/crmLabels';

/**
 * Words for everything the builder shows. Codes never reach the screen: each is translated through
 * the same keys the tabs already use, and a code with no wording is named as "unknown" rather than
 * printed raw.
 */

export const metricKey = (metric: Pick<SmartMetricOption, 'code' | 'variant'>) =>
  metric.variant ? `${metric.code}__${metric.variant}` : metric.code;

export function metricLabel(
  t: TFunction,
  metric: Pick<SmartMetricOption, 'code' | 'variant'>,
): string {
  return t(`reports.smart.metrics.${metricKey(metric)}`);
}

export function metricHint(
  t: TFunction,
  metric: Pick<SmartMetricOption, 'code' | 'variant'>,
): string {
  return t(`reports.smart.metricHints.${metricKey(metric)}`);
}

export function areaLabel(t: TFunction, area: SmartReportArea): string {
  return t(`reports.smart.areas.${area}`);
}

/** A branch means three different things in Customers, and the label says which. */
function branchMeaning(metric: SmartMetricOption | null): string {
  if (metric?.code === 'NEW_CUSTOMERS_REGISTERED') return 'REGISTRATION_BRANCH';
  if (metric?.area === 'CUSTOMERS') return 'ORDER_BRANCH';
  return 'BRANCH';
}

export function dimensionLabel(
  t: TFunction,
  dimension: SmartDimension,
  metric: SmartMetricOption | null,
): string {
  return t(
    `reports.smart.dimensions.${dimension === 'BRANCH' ? branchMeaning(metric) : dimension}`,
  );
}

export function filterLabel(
  t: TFunction,
  filter: SmartFilterCode,
  metric: SmartMetricOption | null,
): string {
  return t(`reports.smart.filters.${filter === 'BRANCH' ? branchMeaning(metric) : filter}`);
}

/** The i18n key family an enumerated value of this dimension or filter is worded under. */
const CODE_KEYS: Partial<Record<SmartDimension | SmartFilterCode, string>> = {
  PAYMENT_METHOD: 'sales.methods',
  AGEING_BUCKET: 'reports.finance.buckets',
  SETTLEMENT_STATUS: 'payables.settlement',
  JOB_STAGE: 'sales.opticalJobs.status',
  MOVEMENT_TYPE: 'reports.inventory.movement',
  PRODUCT_TYPE: 'catalog.types',
  COMM_PURPOSE: 'crm.purpose',
  COMM_STATUS: 'crm.status',
  DELIVERY_MODE: 'reports.crm.mode',
  SUPPRESSION_REASON: 'reports.crm.suppressionReason',
};

export function codeLabel(
  t: TFunction,
  family: SmartDimension | SmartFilterCode,
  code: string,
): string {
  if (family === 'FAILURE_REASON') return failureReasonLabel(t, code);
  const base = CODE_KEYS[family];
  if (!base) return code;
  const key = `${base}.${code}`;
  return t(key, { defaultValue: t('reports.smart.unknownCode') });
}

/** How one breakdown row is named: the tenant's own name, a translated code, or "not recorded". */
export function rowLabel(
  t: TFunction,
  locale: 'en' | 'ar',
  dimension: SmartDimension,
  row: SmartRow,
): string {
  const name = locale === 'ar' ? (row.labelAr ?? row.label) : (row.label ?? row.labelAr);
  if (name) return name;
  if (row.code === 'NO_REGISTRATION_BRANCH') return t('reports.customers.noRegistrationBranch');
  if (row.code) {
    if (dimension === 'BRANCH') return t('reports.smart.unattributed');
    return codeLabel(t, dimension, row.code);
  }
  return t('reports.smart.unattributed');
}

/** The state a WhatsApp or automation branch row is in, as its code says. */
export function branchStateLabel(
  t: TFunction,
  metric: SmartMetricOption,
  code: string | null,
): string | null {
  if (!code) return null;
  if (metric.code === 'AUTOMATION_RULES') return t(`reports.crm.automation.source.${code}`);
  if (code === 'NO_SESSION') return t('reports.smart.noSession');
  return t(`crm.whatsappStatus.${code}`, { defaultValue: t('reports.smart.unknownCode') });
}
